import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Link, Typography, Popover } from '@one-thd/sui-atomic-components';

import { ExperienceContext, useConfigService } from '@thd-nucleus/experience-context';
import { QuantityShipping } from './QuantityShipping';
import { AvailableIcon, UnavailableIcon, PodInformationIcon } from '../../helpers/icon-utils';
import {
  getB2CDeliveryMessage, isB2B, isFunction
} from '../../../components/helper/utils';
import { DELIVERY_TEMPLATES } from '../../templates/templatesConstant';
import {
  getDeliveryMessageToUse,
  productShape,
} from '../../helpers/pod-fulfillment-utils';
import { PodMessage } from '../PodMessage';
import { getPLPSpeedTemplate } from './PLPSpeedTemplate';
import { useTheme } from '@thd-olt-component-react/theme-provider';
import { isAPOFPOZipCode } from '../../helpers/zipcode-helper';

export const DefaultShipping = ({
  applianceDeliveryDate,
  shippingMessageStyle,
  iconType,
  shippingMessage,
  displayQuantity,
  applianceQuantity,
  shippingThreshold,
  excludedState,
  customRender,
  product,
  deliveryMessage,
  hasSameDayFilter,
  isAppliance,
  isNewFulfillmentPodMessagingEnabled,
  isNarrowPod
}) => {
  const experienceContextData = useContext(ExperienceContext);
  const theme = useTheme(DefaultShipping);
  const hideDeliveryEta = theme?.props?.hideDeliveryEta;
  const showFulfillmentEta = !hideDeliveryEta && isNewFulfillmentPodMessagingEnabled &&
    !isAPOFPOZipCode();
  const enablePLPFasterDeliveryOption = !!useConfigService('fs:enablePLPFasterDeliveryOption');
  const { customer } = experienceContextData || {};
  const iconSelector = iconType === 'close' ? UnavailableIcon : AvailableIcon;
  const tooltipMsg = 'Looks like we can\'t deliver this product to your area. '
    + 'Try changing your delivery ZIP to an address in a different state.';

  const shippingCss = classNames(shippingMessageStyle + ' sui-pt-1');
  const excludedShipStatesCss = classNames({
    'sui-text-subtle': true,
    'sui-flex': !enablePLPFasterDeliveryOption,
    'sui-inline-flex sui-flex-wrap': enablePLPFasterDeliveryOption
  });

  const isCustomerB2B = isB2B(customer);

  const deliveryMessageToUse = deliveryMessage ? (getDeliveryMessageToUse[deliveryMessage] || null) : null;

  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const excludedStateTemplate = () => {
    return (
      <div data-component="excludedStateTooltip" className={excludedShipStatesCss}>
        <span className="sui-whitespace-nowrap">Unavailable in&nbsp;</span>
        <span>
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/no-static-element-interactions */}
          <Link
            underline="always"
            aria-describedby={id}
            onClick={handleClick}
            tabIndex="0"
          >
            <span className="sui-text-primary sui-text-sm sui-flex">
              <span>{excludedState}</span>
              <span className="sui-pl-1 sui-inline-flex sui-items-center">{PodInformationIcon}</span>
            </span>
          </Link>
          <Popover
            id={id}
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            placement="top"
            title="Excluded Ship State"
            style={{ zIndex: 900 }}
          >
            <Typography variant="body">
              {tooltipMsg}
            </Typography>
          </Popover>
        </span>
      </div>
    );
  };

  const renderDeliveryMessageElement = (shippingMessageToUse) => (
    <>
      {shippingMessageStyle === 'sui-text-success'
        ? <span className={shippingCss}>{`${shippingMessageToUse} ${deliveryMessageToUse}`}</span>
        : <span className="sui-text-primary">{deliveryMessageToUse}</span>}
    </>
  );

  const renderApplianceMessageElement = (applianceDeliveryCss, shippingMessageToUse) => (
    <>
      <span className={applianceDeliveryCss}>
        {shippingMessageToUse === 'Free' ? shippingMessageToUse : 'Available'}
      </span>
      {' by '}
      <span className="sui-font-bold">{applianceDeliveryDate}</span>
    </>
  );

  const deliveryTemplate = () => {
    let shippingMessageToUse = shippingMessage;
    if (!isCustomerB2B && shippingMessageToUse === 'Standard') {
      shippingMessageToUse = 'Available';
    }
    const applianceDeliveryCss = classNames({
      'sui-text-success': shippingMessageToUse === 'Free'
    });
    return displayQuantity > 0
      ? (
        <QuantityShipping
          displayQuantity={displayQuantity}
          shippingMessageStyle={shippingMessageStyle}
          shippingMessage={shippingMessageToUse}
          shippingThreshold={shippingThreshold}
          deliveryMessageToUse={deliveryMessageToUse}
        />
      ) : (
        <div className={enablePLPFasterDeliveryOption ? 'sui-inline' : ''}>
          {!isAppliance
            && (!deliveryMessageToUse
              ? (<span className={shippingCss}>{getB2CDeliveryMessage(shippingMessageToUse, !isCustomerB2B)}</span>)
              : (renderDeliveryMessageElement(shippingMessageToUse))
            )}
          {applianceDeliveryDate
            && renderApplianceMessageElement(applianceDeliveryCss, shippingMessageToUse)}
        </div>
      );
  };

  if (isFunction(customRender)) {
    return customRender({
      deliveryTemplate: DELIVERY_TEMPLATES.DEFAULT_SHIPPING,
      icon: iconSelector,
      values: {
        excludedState,
        shippingThreshold,
        displayQuantity,
        shippingMessage,
        shippingCss,
        applianceQuantity,
        product,
        experienceContextData
      }
    });
  }

  const template = showFulfillmentEta
    ? () => getPLPSpeedTemplate({ product, displayQuantity, enablePLPFasterDeliveryOption }) : deliveryTemplate;

  // double delivery message element
  if (hasSameDayFilter) {
    return (
      <div className="sui-flex sui-mt-2">
        <div className="sui-pl-6" />
        <Typography variant="body-sm">
          {excludedState ? excludedStateTemplate() : template()}
        </Typography>
      </div>
    );
  }

  return (
    <PodMessage
      messageIcon={iconSelector}
      messageType="Delivery"
      isStoreNameAtEnd={excludedState}
      enablePLPFasterDeliveryOption={enablePLPFasterDeliveryOption}
      isNarrowPod={isNarrowPod}
    >
      <>
        {excludedState ? excludedStateTemplate() : template()}
      </>
    </PodMessage>
  );
};

DefaultShipping.themeProps = {
  hideDeliveryEta: PropTypes.bool,
};

DefaultShipping.defaultThemeProps = {
  hideDeliveryEta: false,
};

DefaultShipping.propTypes = {
  applianceDeliveryDate: PropTypes.string,
  shippingMessageStyle: PropTypes.string,
  shippingMessage: PropTypes.string,
  displayQuantity: PropTypes.number,
  applianceQuantity: PropTypes.number,
  iconType: PropTypes.string,
  isNewFulfillmentPodMessagingEnabled: PropTypes.bool,
  shippingThreshold: PropTypes.number,
  excludedState: PropTypes.string,
  customRender: PropTypes.func,
  product: PropTypes.shape({ productShape, fulfillment: {} }).isRequired,
  deliveryMessage: PropTypes.string,
  hasSameDayFilter: PropTypes.bool,
  isAppliance: PropTypes.bool,
  isNarrowPod: PropTypes.bool
};

DefaultShipping.defaultProps = {
  applianceDeliveryDate: null,
  shippingMessageStyle: 'sui-text-primary',
  iconType: 'close',
  isNewFulfillmentPodMessagingEnabled: false,
  shippingMessage: 'Unavailable',
  displayQuantity: 0,
  applianceQuantity: 0,
  shippingThreshold: null,
  excludedState: null,
  customRender: null,
  deliveryMessage: null,
  hasSameDayFilter: false,
  isAppliance: false,
  isNarrowPod: false
};

DefaultShipping.displayName = 'DefaultShipping';
